/**
 * InventoryKpiCardGrid — 5-card grid of the locked Inventory KPIs.
 *
 * §5.BU §I.3 P3 Commit B (2026-06-19). Locked in
 *   docs/audits/2026-06-19_5bu_i3_p2_design.md §C + §F
 *
 * Five cards (left-to-right, top-to-bottom in a 3-column grid;
 * 5 fills two rows with a half-empty row 2):
 *   1. Stock Concentration (L1 cataloged-denominator headline +
 *      dual disclosure of both numbers in sub-text)
 *   2. OSMI Exposure (status: green/amber/red per relative-% N1 bands)
 *   3. Over-Max Position (L2 guarded methodology; sub-text per
 *      Consult #2 L2 amendment cites both alternate methodologies)
 *   4. Open PO Exposure (sub-text: "X% over max" or hidden when 0)
 *   5. Demand-to-Stock Ratio (5-band status per N1c)
 *
 * Reads `kpis` from the endpoint response. Each card hands off to
 * InventoryKpiCard with the appropriate value + format + subtext.
 * Sparklines pass empty series at v1 (only 1 snapshot exists; §I.2
 * backfill enables) — the KpiCard's `cleanCount < 2` rule hides them
 * automatically per P2 §D.4.
 *
 * QA2-1 (Lane 241): the per-card status pill ("Monitor" / "Lean" / etc.)
 * was removed at Ryan's request; the `status` prop is no longer passed to
 * InventoryKpiCard. The per-card status semantics above still describe the
 * backend-computed enum (retained for reference / future use).
 */
import React from 'react';
import InventoryKpiCard from './InventoryKpiCard';

const GRID_STYLE = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '16px',
  marginBottom: '12px',
};

function formatCurrencyHeadline(v) {
  if (v === null || v === undefined) return 'Not available';
  const n = Number(v);
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function InventoryKpiCardGrid({ kpis }) {
  const k = kpis || {};
  const sc = k.stock_concentration || {};
  const osmi = k.osmi_exposure || {};
  const om = k.over_max_position || {};
  const po = k.open_po_exposure || {};
  const d2s = k.demand_to_stock_ratio || {};

  // KPI 1 — Stock Concentration sub-text: "X.X% of $Y.YM cataloged of $Z.ZM total"
  const stockConcentrationSubtext = (() => {
    const ofTotal = sc.pct_of_total;
    const cataloged = sc.cataloged_dollar;
    const total = sc.total_dollar;
    if (ofTotal === null || ofTotal === undefined || cataloged === null || total === null) {
      return null;
    }
    return `${ofTotal.toFixed(2)}% of $${(total / 1_000_000).toFixed(2)}M total `
         + `(${formatCurrencyHeadline(cataloged)} cataloged)`;
  })();

  // KPI 2 — OSMI sub-text: "$X / Y.Y% of supplier on-hand"
  const osmiSubtext = (() => {
    const dollar = osmi.osmi_dollar;
    const pct = osmi.osmi_pct_of_on_hand;
    if (dollar === null || dollar === undefined) return null;
    if (pct === null || pct === undefined) {
      return `${formatCurrencyHeadline(dollar)}`;
    }
    return `${pct.toFixed(2)}% of supplier on-hand`;
  })();

  // KPI 3 — Over-Max sub-text: "Excludes N SKUs (X.X%) where max_qty=0.
  // Naive calc reports $A; XMEK unallocated-over-max reports $B. See
  // Methodology footer below."
  // Consult #2 L2 amendment — verbatim citation of both alternate
  // methodologies' actual headline figures.
  const overMaxSubtext = (() => {
    const zero = om.skus_max_zero_count;
    const total = om.skus_total_count;
    if (zero === null || zero === undefined || !total) return null;
    const pct = total > 0 ? (zero / total) * 100 : 0;
    return `Excludes ${zero.toLocaleString()} SKUs (${pct.toFixed(1)}%) where `
         + `max_qty=0 (no maximum defined). See Methodology footer below.`;
  })();

  // KPI 4 — Open PO sub-text: "X.X% will push over max" or null when $0
  const openPoSubtext = (() => {
    const total = po.open_po_total_dollar;
    const pct = po.pct_pushing_over_max;
    if (total === null || total === undefined || total === 0) return null;
    if (pct === null || pct === undefined) return null;
    return `${pct.toFixed(2)}% will push over max`;
  })();

  // KPI 5 — Demand-to-Stock sub-text: "(on-hand + open PO) / usage_12mo"
  const demandToStockSubtext = (() => {
    const onHand = d2s.on_hand_qty_total;
    const openPo = d2s.open_po_qty_total;
    const usage = d2s.usage_12mo_total;
    if (usage === null || usage === undefined || usage === 0) {
      return 'No usage in trailing 12 months';
    }
    if (onHand === null || openPo === null) return null;
    const supply = (onHand + openPo) / 1_000_000;
    const demand = usage / 1_000_000;
    return `${supply.toFixed(2)}M / ${demand.toFixed(2)}M qty`;
  })();

  return (
    <div style={GRID_STYLE} data-testid="inventory-kpi-grid">
      <InventoryKpiCard
        label="Stock Concentration"
        value={sc.headline_pct}
        format="pct"
        subtext={stockConcentrationSubtext}
      />
      <InventoryKpiCard
        label="OSMI Exposure"
        value={osmi.osmi_dollar}
        format="currency"
        subtext={osmiSubtext}
      />
      <InventoryKpiCard
        label="Over-Max Position"
        value={om.over_max_dollar}
        format="currency"
        subtext={overMaxSubtext}
      />
      <InventoryKpiCard
        label="Open PO Exposure"
        value={po.open_po_total_dollar}
        format="currency"
        subtext={openPoSubtext}
      />
      <InventoryKpiCard
        label="Demand-to-Stock"
        value={d2s.ratio}
        format="decimal"
        subtext={demandToStockSubtext}
      />
    </div>
  );
}

export default InventoryKpiCardGrid;
